/** The four-tier CapX Ops authority ladder (server: `users.ops_tier`). */
export type OpsTier = 'ANALYST' | 'SENIOR_OPS' | 'CREDIT_COMMITTEE';

/** Display labels for the Ops tier — the canon authority labels. */
export const OPS_TIER_LABELS: Record<OpsTier, string> = {
  ANALYST: 'Analyst',
  SENIOR_OPS: 'Senior Ops',
  CREDIT_COMMITTEE: 'Credit Committee',
};

export const OPS_TIER_OPTIONS: { value: OpsTier; label: string }[] = [
  { value: 'ANALYST', label: 'Analyst' },
  { value: 'SENIOR_OPS', label: 'Senior Ops' },
  { value: 'CREDIT_COMMITTEE', label: 'Credit Committee' },
];

/** The cumulative MCP access grant tier (server: `users.mcp_access_tier`). */
export type McpAccessTier = 'LOW' | 'MEDIUM' | 'HIGH';

/** Short label for the MCP tier — used on the row chip. */
export const MCP_TIER_LABELS: Record<McpAccessTier, string> = {
  LOW: 'Overview',
  MEDIUM: 'Applications',
  HIGH: 'Full',
};

/**
 * The grant options shown in the MCP access modal — `null` is the explicit
 * "No access" choice. Each carries the one-line description of what the tier
 * exposes (from the MCP design spec §2).
 */
export const MCP_ACCESS_OPTIONS: {
  value: McpAccessTier | null;
  label: string;
  description: string;
}[] = [
  {
    value: null,
    label: 'No access',
    description: 'The user cannot connect to the MCP server. This is the default.',
  },
  {
    value: 'LOW',
    label: 'Overview',
    description:
      'Counts, status breakdowns, SLA/aging, pipeline funnels, system health, AI-spend totals. No application-level rows.',
  },
  {
    value: 'MEDIUM',
    label: 'Applications',
    description:
      'Everything in Overview, plus individual KS/DF applications — with all PII stripped.',
  },
  {
    value: 'HIGH',
    label: 'Full (PII)',
    description:
      'Everything above, with PII unredacted. Grants unredacted personal-data export to an external AI client — POPIA-sensitive.',
  },
];

/** A SYSTEM role assigned to a user (slug + display name). */
interface UserRole {
  slug: string;
  name: string;
}

/** A SYSTEM role offered in the role picker. */
export interface AdminRole {
  id: string;
  name: string;
  slug: string;
  description: string;
}

/** A user row in the admin Users list / detail. */
export interface AdminUser {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  is_active: boolean;
  email_verified_at: string | null;
  ops_tier: OpsTier | null;
  is_credit_committee_member: boolean;
  notify_new_applications: boolean;
  mcp_access_tier: McpAccessTier | null;
  mcp_access_label: string | null;
  last_login: string | null;
  date_joined: string;
  system_roles: UserRole[];
}

export interface AdminUsersResponse {
  users: AdminUser[];
}

export interface AdminRolesResponse {
  roles: AdminRole[];
}

/** Payload to create a new staff user (they receive a set-password invite). */
export interface CreateUserPayload {
  first_name: string;
  last_name: string;
  email: string;
  role_slugs?: string[];
  ops_tier?: OpsTier | null;
  is_credit_committee_member?: boolean;
  notify_new_applications?: boolean;
}

/** Patch a user's authority fields. */
export interface UpdateUserPayload {
  ops_tier?: OpsTier | null;
  is_credit_committee_member?: boolean;
  notify_new_applications?: boolean;
  is_active?: boolean;
}
